import { all, call, delay, put, race, select, take, takeEvery } from 'redux-saga/effects';
import { ocrVerificationActions } from './ocrVerificationSlice';
import { selectOcrAttempt } from './ocrVerificationSelectors';
import { IMAGE_UPLOAD_STATUS, transactionImageActions } from 'store/storeTransactionImage/storeTransactionImageSlice';
import { selectTransactionBatches } from 'store/storeTransactionImage/storeTransactionImageSelector';
import { snackbarActions } from 'store/snackbarsSlice/snackbarsSlice';
import { transactionActions } from 'store/transactionsSlice/transactionsSlice';
import { requestOcrExtraction } from 'services/ocrVerificationService';
import { OCR_EXTRACTION_STATUS, OCR_IDENTIFIER_LABEL } from 'utils/ocrVerification';

// The snapshot goes to S3 through the transaction media upload first; Condor reads it from there. Long enough for
// the upload's own retries (2 + 4 + 8 s) and a batch queued ahead of it.
export const OCR_SNAPSHOT_UPLOAD_TIMEOUT_MS = 60000;

const KNOWN_RESULT_STATUSES = new Set([OCR_EXTRACTION_STATUS.EXTRACTED, OCR_EXTRACTION_STATUS.UNREADABLE, OCR_EXTRACTION_STATUS.FAILED]);

const findSnapshotUpload = (batches, transactionId, snapshotId) => {
    for (const batch of batches || []) {
        if (String(batch.transactionId) !== String(transactionId)) continue;
        const image = batch.images?.find(({ id }) => String(id) === String(snapshotId));
        if (image) return image;
    }
    return null;
};

/**
 * The asset id of the snapshot once its S3 upload is done, or null when the upload failed for good. Reads the media
 * upload only through its batches and actions, which the upload pipeline keeps for every agent snapshot.
 */
export function* waitForSnapshotAsset(transactionId, snapshotId) {
    while (true) {
        const image = findSnapshotUpload(yield select(selectTransactionBatches), transactionId, snapshotId);
        if (image?.status === IMAGE_UPLOAD_STATUS.SAVED && image.assetId) return image.assetId;
        // a failed presign has no URL and is not retried; a failed upload is final once its retries ran out
        if (image?.isFinalFailed || (image?.status === IMAGE_UPLOAD_STATUS.FAILED && !image.presignPutUrl)) return null;
        yield take([transactionImageActions.appendBatch.type, transactionImageActions.setImageStatus.type]);
    }
}

const failureMessage = (err) => {
    if (err?.code === 'ECONNABORTED') return 'OCR request timed out';
    return err?.response?.data?.error || 'OCR request failed';
};

function* notify(message, variant) {
    yield put(snackbarActions.enqueueSnackbar({ message, options: { variant } }));
}

function* isAttemptPending(transactionId, attemptId) {
    const attempt = yield select(selectOcrAttempt(transactionId, attemptId));
    return attempt?.status === OCR_EXTRACTION_STATUS.PENDING;
}

export function* runOcrExtraction({ payload: { transactionId, attemptId, identifier, snapshotId } }) {
    // not stored: the same identifier already had a read in flight
    if (!(yield call(isAttemptPending, transactionId, attemptId))) return;

    const label = OCR_IDENTIFIER_LABEL[identifier];
    const complete = (result) => put(ocrVerificationActions.extractionCompleted({ transactionId, attemptId, completedAt: Date.now(), ...result }));
    // the transaction closed while its read was in flight: its outcome has nowhere to go, so no toast either
    const isDropped = function* () {
        if (yield call(isAttemptPending, transactionId, attemptId)) return false;
        console.log(`runOcrExtraction(): transactionId=${transactionId}, identifier=${identifier} - dropped, the transaction was closed`);
        return true;
    };

    const { assetId } = yield race({
        assetId: call(waitForSnapshotAsset, transactionId, snapshotId),
        timeout: delay(OCR_SNAPSHOT_UPLOAD_TIMEOUT_MS),
    });
    if (yield call(isDropped)) return;
    if (!assetId) {
        console.warn(`runOcrExtraction(): transactionId=${transactionId}, identifier=${identifier} - the OCR snapshot was not uploaded`);
        yield complete({ status: OCR_EXTRACTION_STATUS.FAILED, message: 'The photo could not be saved' });
        yield call(notify, `Get text from photo (${label}): the photo could not be saved. Try again.`, 'error');
        return;
    }

    try {
        const result = yield call(requestOcrExtraction, transactionId, { assetId, identifier, clientAttemptId: attemptId });
        if (yield call(isDropped)) return;
        const status = KNOWN_RESULT_STATUSES.has(result?.status) ? result.status : OCR_EXTRACTION_STATUS.FAILED;
        yield complete({
            status,
            value: status === OCR_EXTRACTION_STATUS.EXTRACTED ? result.value : null,
            assetId,
            condorRequestId: result?.condorRequestId ?? null,
            message: result?.message ?? null,
        });
        console.log(
            `runOcrExtraction(): transactionId=${transactionId}, identifier=${identifier}, status=${status}, condorRequestId=${result?.condorRequestId}`
        );
        if (status === OCR_EXTRACTION_STATUS.UNREADABLE) yield call(notify, `Get text from photo: no ${label} found in the photo.`, 'warning');
        if (status === OCR_EXTRACTION_STATUS.FAILED) {
            yield call(notify, `Get text from photo (${label}) failed: ${result?.message || 'OCR request failed'}.`, 'error');
        }
    } catch (err) {
        const message = failureMessage(err);
        console.error(`runOcrExtraction(): transactionId=${transactionId}, identifier=${identifier} - ${message}`, err);
        if (yield call(isDropped)) return;
        yield complete({ status: OCR_EXTRACTION_STATUS.FAILED, assetId, message });
        yield call(notify, `Get text from photo (${label}) failed: ${message}.`, 'error');
    }
}

// After a submit or discard the panel closes through setFormsObj, not removeForm, so the reads are dropped here.
// Kept, they would outlive the transaction and a reopen in the same session would inherit their Match.
export function* clearOcrOnTransactionClose({ payload }) {
    const { transactionId, closeAllPanels } = payload || {};
    if (closeAllPanels) {
        yield put(ocrVerificationActions.allTransactionsClosed());
    } else if (transactionId != null) {
        yield put(ocrVerificationActions.transactionClosed(transactionId));
    }
}

// takeEvery, never takeLatest: reads of different identifiers or transactions run side by side and none cancels
// another; one identifier is kept to one read at a time by the reducer.
export function* ocrVerificationSaga() {
    yield all([
        takeEvery(ocrVerificationActions.extractionRequested.type, runOcrExtraction),
        takeEvery(transactionActions.closeTransactionPanel.type, clearOcrOnTransactionClose),
    ]);
}
